import type { MenuProps } from 'antd'
import { Menu } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { MENU_KEY, SIDEBAR_MENU, SidebarMenuItem } from 'constants/menu'
import { usePermission } from 'hooks/use-permission'

import './style.scss'

type MenuItem = Required<MenuProps>['items'][number]

const Sidebar = () => {
  const { canView } = usePermission()

  const getItem = (item: SidebarMenuItem, children?: MenuItem[] | null): MenuItem => {
    return {
      key: item.value,
      icon: item.icon,
      label: <Link to={item.link as string}>{item.label}</Link>,
      children: children?.length ? children : null,
      type: children?.length ? 'submenu' : 'item',
    } as MenuItem
  }

  const isItemVisible = (item: SidebarMenuItem): boolean => {
    if (item.applyAll) return true
    if (item.feature) return canView(item.feature)
    if (item.children?.length) return item.children.some(isItemVisible)
    return false
  }

  const renderMenuSidebar = (sidebarMenu: SidebarMenuItem[]): MenuItem[] => {
    const menu = sidebarMenu.filter((c) => c.isShow)
    return menu.length
      ? (menu
          .map((item) => {
            if (!isItemVisible(item)) return null
            const children = item.children?.length ? renderMenuSidebar(item.children) : null
            return getItem(item, children)
          })
          .filter((x) => x !== null) as MenuItem[])
      : []
  }

  const items: MenuItem[] = renderMenuSidebar(SIDEBAR_MENU)

  const location = useLocation()

  const pathMatches = (path: string, route: string) => {
    return path === route || path.startsWith(route + '/')
  }

  const findMatchingMenuKey = useCallback((path: string, routes: any[]): any => {
    const matchMenuItems: any[] = []
    routes.map((route) => {
      const matchMenu = route.link ? pathMatches(path, route.link) : false
      if (route.children?.length) {
        const matchChildren = findMatchingMenuKey(path, route.children)
        if (matchChildren?.length || matchMenu) {
          matchMenuItems.push(...[route, ...matchChildren])
        }
      } else {
        if (matchMenu) {
          matchMenuItems.push(route)
        }
      }
    })
    return matchMenuItems
  }, [])

  const getKeyFromPath = useCallback(() => {
    const path = location.pathname
    const activeMenu = findMatchingMenuKey(path, SIDEBAR_MENU)
    return activeMenu?.length ? activeMenu?.map((c: any) => c.value) : MENU_KEY.DASHBOARD
  }, [findMatchingMenuKey, location.pathname])

  const [selectedKey, setSelectedKey] = useState([])
  useEffect(() => {
    const selectedMenu = getKeyFromPath()
    setSelectedKey(selectedMenu)
  }, [getKeyFromPath, location.pathname])

  return (
    <>
      <Menu mode="inline" selectedKeys={selectedKey} style={{ width: 256 }} items={items} />
    </>
  )
}
export default Sidebar
